import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OrderDetails.css';
import OrderTrackingTimeline from './OrderTrackingTimeline';
import ReviewModal from './ReviewModal';
import { API, authHeadersOnly } from '../services/apiConfig';
import {
  FaArrowLeft, FaFileInvoice, FaCalendarAlt, FaCheckCircle,
  FaCreditCard, FaMoneyBillWave, FaTruck, FaMapMarkerAlt, FaBox,
  FaTag, FaPercent, FaShippingFast, FaReceipt, FaDownload,
  FaRedo, FaStar, FaBan, FaExclamationTriangle
} from 'react-icons/fa';

const resolveImg = (url) => {
  if (!url) return '/images/product.jpg';
  if (url.startsWith('http')) return url;
  return `${API}${url}`;
};

const STATUS_CFG = {
  PENDING:          { label: 'Pending',          color: '#d97706', bg: '#fef3c7' },
  CONFIRMED:        { label: 'Confirmed',         color: '#1d4ed8', bg: '#dbeafe' },
  PACKED:           { label: 'Packed',            color: '#6d28d9', bg: '#ede9fe' },
  SHIPPED:          { label: 'Shipped',           color: '#0e7490', bg: '#cffafe' },
  OUT_FOR_DELIVERY: { label: 'Out for Delivery',  color: '#065f46', bg: '#d1fae5' },
  DELIVERED:        { label: 'Delivered',         color: '#166534', bg: '#dcfce7' },
  CANCELLED:        { label: 'Cancelled',         color: '#991b1b', bg: '#fee2e2' },
  RETURNED:         { label: 'Returned',          color: '#374151', bg: '#f3f4f6' },
};

function InfoRow({ icon, label, value, badge }) {
  return (
    <div className="od-info-row">
      <span className="od-info-icon">{icon}</span>
      <span className="od-info-label">{label}</span>
      {badge ? (
        <span className="od-info-badge" style={{ color: badge.color, background: badge.bg }}>{value}</span>
      ) : (
        <span className="od-info-value">{value}</span>
      )}
    </div>
  );
}

function OrderDetails({ order, user, onBack }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [reviewedProducts, setReviewedProducts] = useState(new Set());

  useEffect(() => { fetchOrderDetails(); }, []);

  const fetchOrderDetails = async () => {
    try {
      const { data } = await axios.get(`${API}/api/orders/${order.id}/details?userId=${user.userId}`, { headers: authHeadersOnly() });
      setDetails(data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.put(`${API}/api/orders/${order.id}/cancel?userId=${user.userId}`, null, { headers: authHeadersOnly() });
      alert('Order cancelled successfully');
      onBack();
    } catch (err) {
      alert(err.response?.data || 'Failed to cancel order');
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/orders/${order.id}/invoice?userId=${user.userId}`,
        { responseType: 'blob', headers: authHeadersOnly() }
      );
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', `invoice-${order.id}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
    } catch { alert('Failed to download invoice'); }
  };

  if (loading) return (
    <div className="od-loading">
      <div className="od-spin" />
      <p>Loading order details…</p>
    </div>
  );
  if (!details) return (
    <div className="od-error">
      <FaExclamationTriangle /> Failed to load order details
    </div>
  );

  let orderItems = [];
  try { orderItems = order.items ? JSON.parse(order.items) : []; } catch {}

  const canCancel = ['PENDING','CONFIRMED','PACKED'].includes(details.status);
  const canReview = details.status === 'DELIVERED';
  const canTrack  = ['SHIPPED','OUT_FOR_DELIVERY'].includes(details.status);
  const statusCfg = STATUS_CFG[details.status] || STATUS_CFG.PENDING;

  return (
    <div className="od-container">

      {/* ── Header ── */}
      <div className="od-header">
        <button className="od-back-btn" onClick={onBack}>
          <FaArrowLeft /> Back to Orders
        </button>
        <div className="od-header-center">
          <span className="od-header-title">Order Details</span>
        </div>
        <span className="od-header-badge" style={{ color: statusCfg.color, background: statusCfg.bg }}>
          {statusCfg.label}
        </span>
      </div>

      <div className="od-body">

{/* ── Order Info ── */}
        <div className="od-card">
          <div className="od-card-title"> Order Information</div>
          <div className="od-info-grid">
            <InfoRow label="Invoice No."     value={details.invoiceNumber || '—'} />
            <InfoRow label="Order Date"      value={new Date(details.orderDate).toLocaleString('en-IN')} />
            <InfoRow label="Status"          value={statusCfg.label} badge={statusCfg} />
            <InfoRow label="Payment Method"  value={details.paymentMethod || 'COD'} />
            <InfoRow label="Payment Status"  value={details.paymentStatus} />
            {details.estimatedDeliveryDate && (
              <InfoRow label="Est. Delivery" value={new Date(details.estimatedDeliveryDate).toLocaleDateString('en-IN')} />
            )}
            {details.actualDeliveryDate && (
              <InfoRow icon={<FaCheckCircle />} label="Delivered On" value={new Date(details.actualDeliveryDate).toLocaleDateString('en-IN')} />
            )}
          </div>
        </div>

        {/* ── Tracking ── */}
        <OrderTrackingTimeline status={details.status} trackingNumber={details.trackingNumber} courierPartner={details.courierPartner} />

        {/* ── Review prompt ── */}
        {canReview && (
          <div className="od-review-prompt">
            <FaStar className="od-review-star" />
            <span>Your order was delivered! Click <strong>Write Review</strong> on each item below to share your experience.</span>
          </div>
        )}

        {/* ── Order Items ── */}
        <div className="od-card">
          <div className="od-card-title"> Order Items <span className="od-items-count">{orderItems.length} item{orderItems.length !== 1 ? 's' : ''}</span></div>
          {orderItems.length > 0 ? (
            <div className="od-items-list">
              {orderItems.map((item, i) => (
                <div key={i} className="od-item">
                  <img src={resolveImg(item.image)} alt={item.productName} className="od-item-img" onError={e => { e.target.src = '/images/product.jpg'; }} />
                  <div className="od-item-info">
                    <div className="od-item-name">{item.productName}</div>
                    <div className="od-item-meta">
                      {item.variantColor && <span><FaTag style={{fontSize:'10px'}} /> {item.variantColor}</span>}
                      {item.variantSize  && <span><FaBox style={{fontSize:'10px'}} /> {item.variantSize}</span>}
                      <span><FaBox style={{fontSize:'10px'}} /> Qty: {item.quantity}</span>
                    </div>
                    <div className="od-item-price">
                      ₹{item.price} × {item.quantity} = <strong>₹{item.price * item.quantity}</strong>
                    </div>
                    {canReview && item.productId && !reviewedProducts.has(item.productId) && (
                      <button className="od-btn-review" onClick={() => { setSelectedProductId(item.productId); setSelectedProductName(item.productName || ''); setShowReviewModal(true); }}>
                        <FaStar /> Write Review
                      </button>
                    )}
                    {reviewedProducts.has(item.productId) && (
                      <span className="od-reviewed-tag">✓ Reviewed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="od-no-items">No items found</p>}
        </div>

        {/* ── Shipping ── */}
        <div className="od-card">
          <div className="od-card-title"> Shipping Address</div>
          <p className="od-address">{details.shippingAddress}</p>
        </div>

        {/* ── Summary ── */}
        <div className="od-card">
          <div className="od-card-title"> Order Summary</div>
          <div className="od-summary">
            <div className="od-sum-row"><span> Subtotal</span><span>₹{details.subtotal || details.totalAmount}</span></div>
            {details.discount > 0 && (
              <div className="od-sum-row od-sum-discount">
                <span> Discount {details.couponCode && `(${details.couponCode})`}</span>
                <span>−₹{details.discount}</span>
              </div>
            )}
            <div className="od-sum-row"><span> Delivery Charge</span><span>₹{details.deliveryCharge || 0}</span></div>
            <div className="od-sum-row"><span> Tax (GST)</span><span>₹{details.tax || 0}</span></div>
            <div className="od-sum-row od-sum-total"><span>Total Amount</span><span>₹{details.totalAmount}</span></div>
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="od-actions">
          {canTrack && (
            <button className="od-btn od-btn-track"><FaTruck /> Track Order</button>
          )}
          <button className="od-btn od-btn-invoice" onClick={handleDownloadInvoice}><FaDownload /> Download Invoice</button>
          {details.status === 'DELIVERED' && (
            <button className="od-btn od-btn-buy" onClick={() => {
              const firstItem = orderItems[0];
              if (firstItem?.productId) {
                window.location.href = `/products?q=${encodeURIComponent(firstItem.productName)}`;
              } else {
                window.location.href = '/products';
              }
            }}><FaRedo /> Buy Again</button>
          )}
        </div>

      </div>

      {showReviewModal && (
        <ReviewModal
          orderId={order.id}
          productId={selectedProductId}
          productName={selectedProductName}
          userId={user.userId}
          userName={(user.name && user.name !== 'null' && user.name.trim()) ? user.name : ''}
          userEmail={user.email || ''}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => setReviewedProducts(prev => new Set([...prev, selectedProductId]))}
        />
      )}
    </div>
  );
}

export default OrderDetails;
